import { Search } from "@/components/search/Search.tsx";
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  FontAwesomeIcon,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface AddFilterField {
  id: string;
  label: string;
  submenu?: string;
  group?: string;
  shortLabel?: string;
}

interface AddFilterMenuProps {
  fields: AddFilterField[];
  onSelect: (id: string) => void;
}

export function AddFilterMenu({ fields, onSelect }: AddFilterMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = fields.filter((field) =>
    field.label.toLowerCase().includes(search.toLowerCase())
  );

  const topLevel = search ? filtered : filtered.filter((field) => !field.submenu);
  const submenus = search
    ? []
    : [...new Set(filtered.flatMap((field) => (field.submenu ? [field.submenu] : [])))];

  const renderItem = (field: AddFilterField, title: string) => (
    <DropdownMenuItem key={field.id} title={title} onClick={() => handleSelect(field.id)} />
  );

  const renderSubmenuItems = (fields: AddFilterField[]) =>
    groupFields(fields).flatMap(([group, items]) => [
      ...(group ? [<DropdownMenuLabel key={`label-${group}`}>{group}</DropdownMenuLabel>] : []),
      ...items.map((field) => renderItem(field, field.shortLabel ?? field.label)),
    ]);

  const handleSelect = (id: string) => {
    onSelect(id);
    setSearch("");
    setOpen(false);
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
      closeOnClick={false}
      trigger={
        <Button
          variant="plain"
          size="sm"
          className="justify-start px-0"
          leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
        >
          {t("common:add_filter")}
        </Button>
      }
    >
      <Box className="w-56 p-2 pt-0">
        <Search
          value={search}
          placeholder={t("reporting:people_analytics_filter_search")}
          clearable
          onClear={() => setSearch("")}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </Box>
      {topLevel.map((field) => renderItem(field, field.label))}
      {submenus.map((submenu) => (
        <DropdownMenuSub key={submenu} title={submenu}>
          {renderSubmenuItems(filtered.filter((field) => field.submenu === submenu))}
        </DropdownMenuSub>
      ))}
    </DropdownMenu>
  );
}

function groupFields(fields: AddFilterField[]) {
  const groups = new Map<string, AddFilterField[]>();
  fields.forEach((field) => {
    const key = field.group ?? "";
    groups.set(key, [...(groups.get(key) ?? []), field]);
  });
  return [...groups.entries()];
}
